import React from 'react';
import { Modal, Backdrop, Fade, CircularProgress } from '@mui/material';
import styles from './PaymentStatusModal.module.scss';
import { PaymentStatus } from '../../../types/Booking/booking';
import { usePaymentStatusModal } from './hooks/usePaymentStatusModal';

interface PaymentStatusModalProps {
  open: boolean;
  status: PaymentStatus | null;
  onClose: () => void;
  onRetry: () => void;
}

const PaymentStatusModal: React.FC<PaymentStatusModalProps> = ({
  open,
  status,
  onClose,
  onRetry
}) => {
  const {
    t,
    config,
    isProcessing,
    showRetryButton,
    showViewDetailsButton,
    handleGoToProfessionals
  } = usePaymentStatusModal(status);

  if (!status || !open) return null;

  const { Icon, color, eyebrow, title, message } = config;

  const getStatusClass = (col: string) => {
    switch (col.toLowerCase()) {
      case '#3b82f6':
        return 'bw-status-icon--info';
      case '#22c55e':
        return 'bw-status-icon--success';
      case '#ef4444':
        return 'bw-status-icon--error';
      case '#f97316':
        return 'bw-status-icon--warning';
      default:
        return 'bw-status-icon--neutral';
    }
  };

  const statusClass = getStatusClass(color);

  return (
    <Modal
      open={open}
      onClose={isProcessing ? undefined : onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
        className: styles.modalBackdrop,
      }}
    >
        <Fade in={open}>
            <div className={styles.modalContent}>
              <article className="bw-modal-card bw-modal-card--status" role="dialog" aria-modal="true" aria-labelledby="payment-status-title">
              <div className={`bw-status-icon ${statusClass}`}>
                {isProcessing ? (
                  <CircularProgress size={70} sx={{ color: color }} />
                ) : (
                  Icon && <Icon className="bw-status-icon__svg" />
                )}
              </div>

              {eyebrow && <span className="bw-modal-eyebrow">{eyebrow}</span>}
              <h2 id="payment-status-title" className="bw-modal-title">{title}</h2>
              <p className="bw-modal-copy">{message}</p>

              {!isProcessing && (
                <div className="bw-modal-actions">
                  {showRetryButton && (
                    <button className="bw-btn bw-btn-primary bw-btn-full" onClick={onRetry}>{t('appAdmin.PaymentStatusModal.retryPayment')}</button>
                  )}

                  {showViewDetailsButton && (
                    <button className="bw-btn bw-btn-primary bw-btn-full" onClick={onClose}>{t('appAdmin.PaymentStatusModal.viewBookingDetails')}</button>
                  )}

                  <button className="bw-btn bw-btn-secondary bw-btn-full" onClick={handleGoToProfessionals}>{t('appAdmin.PaymentStatusModal.continueExploring')}</button>
                </div>
              )}
            </article>
          </div>
        </Fade>
    </Modal>
  );
};

export default PaymentStatusModal;